import type { ComponentType, ForwardedRef, ReactElement } from "react";
import { forwardRef, useCallback, useMemo, useState } from "react";

import type {
    IDropdownButtonProps,
    IDropdownChild,
    IDropDownItem,
    IDropdownProps,
    IEllipsisDropdownItemProps,
} from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import BaseMultiselectDropdown from "app/pages/sales-intelligence/common-components/dropdown/BaseMultiselectDropdown/BaseMultiselectDropdown";
import useSortedDropdownItems from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSortedDropdownItems";
import { buildDropdownSelectedItemsFromArray } from "app/pages/sales-intelligence/utils/dropdowns";

import { StyledSignalFilterBlock } from "../styles";

import { DROPDOWN_DEFAULT_HEIGHT, DROPDOWN_DEFAULT_WIDTH, SELECTED_ALL_ID } from "./const";
import { getDropdownChildSortingIteratee } from "./utils";

type FilterMultiselectDropdownProps<Value> = Partial<Omit<IDropdownProps<Value>, "children">> & {
    filterKey: string;
    isFullSelection: boolean;
    processOptions?: boolean;
    selectedOptions: ReadonlyArray<Value>;
    buttonComponent: ComponentType<Pick<IDropdownButtonProps, "isOpen" | "disabled">>;
    selectAllComponent: ComponentType<IEllipsisDropdownItemProps>;
    renderDropdownItems?: (
        dropDownButton: ReactElement,
        selectedAllItem: IDropdownChild,
        items: readonly IDropdownChild[],
    ) => IDropdownChild[];
    children: readonly IDropdownChild[];
    onChange(item: Value | Value[] | null, isSelected: boolean): void;
};

const FilterMultiselectDropdown = <Ref,>(
    props: FilterMultiselectDropdownProps<string | number>,
    ref: ForwardedRef<Ref>,
) => {
    const {
        filterKey,
        isFullSelection,
        processOptions = false,
        selectedOptions,
        width = DROPDOWN_DEFAULT_WIDTH,
        dropdownPopupHeight = DROPDOWN_DEFAULT_HEIGHT,
        hasSearch,
        buttonComponent: DropdownButton,
        selectAllComponent: SelectAllItem,
        children,
        onChange,
        onToggle,
        renderDropdownItems,
        ...dropdownProps
    } = props;

    const isEmptySelection = selectedOptions.length === 0;

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const selectedItems = useMemo(() => {
        return buildDropdownSelectedItemsFromArray(selectedOptions, {
            [SELECTED_ALL_ID]: isFullSelection,
        });
    }, [isFullSelection, selectedOptions]);

    const items = useSortedDropdownItems({
        shouldProcess: processOptions && isDropdownOpen,
        collection: children,
        iteratee: getDropdownChildSortingIteratee(selectedItems),
    });

    const handleItemClick = useCallback(
        // Dropdown passes null on Enter when the active item is filtered out by the search term
        (item?: IDropDownItem | null) => {
            if (!item) {
                return;
            }

            const { id = "" } = item;
            const valueId = id === SELECTED_ALL_ID ? null : id;

            onChange(valueId, selectedItems[id]);
        },
        [onChange, selectedItems],
    );

    const handleUncontrolledToggle = useCallback(
        (...params: Parameters<NonNullable<typeof onToggle>>): void => {
            const [isOpen] = params;

            setIsDropdownOpen(isOpen);
            onToggle?.(...params);
        },
        [onToggle],
    );

    const getDropDownItems = (): IDropdownChild[] => {
        const dropdownButton = (
            <DropdownButton
                disabled={dropdownProps.disabled}
                isOpen={isDropdownOpen}
                key={filterKey}
            />
        );

        if (typeof renderDropdownItems === "function") {
            return renderDropdownItems(
                dropdownButton,
                <SelectAllItem
                    key={SELECTED_ALL_ID}
                    id={SELECTED_ALL_ID}
                    partiallySelected={!isEmptySelection ? !isFullSelection : null}
                />,
                items,
            );
        }

        return [
            dropdownButton,
            <SelectAllItem
                key={SELECTED_ALL_ID}
                id={SELECTED_ALL_ID}
                partiallySelected={!isEmptySelection ? !isFullSelection : null}
            />,
            ...items,
        ];
    };

    return (
        <StyledSignalFilterBlock id={filterKey}>
            <BaseMultiselectDropdown
                {...dropdownProps}
                ref={ref}
                width={width}
                dropdownPopupHeight={dropdownPopupHeight}
                appendTo={`#${filterKey}`}
                selectedIds={selectedItems}
                hasSearch={hasSearch}
                searchPlaceHolder={translate(`si.signals.filters.${filterKey}.search.placeholder`)}
                noResultsMessage={translate("dropdown.no.results")}
                onClick={handleItemClick}
                onToggle={handleUncontrolledToggle}
            >
                {getDropDownItems()}
            </BaseMultiselectDropdown>
        </StyledSignalFilterBlock>
    );
};

export default forwardRef(FilterMultiselectDropdown);
